import React, { Suspense, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect, Provider, useDispatch } from 'react-redux';
import { useRoutes } from 'react-router-dom';
import './app.scss';
import { CustomProvider } from '@leegality/leegality-react-component-library';
import configureStore from '../store/configure-store';
import TranslationsContext from '../store/translations-context';
import InitialReducers from './reducers';
import { appMounted } from './actions/app-actions';
import appRoutes from '../constants/app-routes';
import Loading from '../components/views/loading';
import { NotificationBase } from '../components/views/notification/notification';
import { resetRouting } from './reducers/routing-reducer';
import SafeRedirect from '../components/views/safe-redirect';
import AuthManager from '../store/auth-manager';
import useAuthorisedList from '../components/enhancers/custom-hook/use-authorised-list';
import useModalManager from '../components/enhancers/custom-hook/use-modal-manager';
import useMobileView from '../components/enhancers/custom-hook/use-mobile-view';
import { filterMobileUnsupportedRoutes } from '../utils/general-utils';

const mapState = (reducers) => (reducers);

const App = ({ translations, routing, user }) => {
  const dispatch = useDispatch();

  const handleRedirectCleanUp = () => {
    dispatch(resetRouting());
  };

  const loadingView = user.loaded ? null : <Loading />;
  useModalManager('main');
  const [authorisedRoutes] = useAuthorisedList(appRoutes);
  const routes = user.loaded ? authorisedRoutes : [];
  const isMobileView = useMobileView();
  const filteredRoutes = isMobileView
    ? filterMobileUnsupportedRoutes(routes)
    : routes;

  return (
    <>
      <SafeRedirect
        path={routing.redirectTo}
        onCleanUp={handleRedirectCleanUp}
        navigateState={routing.navigateState}
      />
      <TranslationsContext.Provider value={translations}>
        <CustomProvider locale={user.selectedLocale}>
          <NotificationBase />
          <Suspense fallback={<Loading />}>
            {useRoutes(filteredRoutes)}
            {loadingView}
          </Suspense>
        </CustomProvider>
      </TranslationsContext.Provider>
    </>
  );
};

App.propTypes = {
  translations: PropTypes.instanceOf(Object),
  routing: PropTypes.instanceOf(Object),
  user: PropTypes.instanceOf(Object),
};

App.defaultProps = {
  translations: {},
  routing: {},
  user: {},
};

const ConnectedApp = connect(mapState, () => ({}))(App);

const AppRoot = () => {
  const storeRef = useRef(null);
  const appMountedFiredRef = useRef(false);

  if (storeRef.current === null) {
    storeRef.current = configureStore({}, InitialReducers);
    AuthManager.init(storeRef.current);
  }

  useEffect(() => {
    if (!appMountedFiredRef.current) {
      appMountedFiredRef.current = true;
      storeRef.current.dispatch(appMounted());
    }
  }, []);

  return (
    <Provider store={storeRef.current}>
      <ConnectedApp />
    </Provider>
  );
};

export default AppRoot;
